import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronRightIcon,
  SparklesIcon,
  MicrophoneIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { uploadFile } from "../../utils/helperMethods";
import VoiceRecoder from "../../components/AudioRecorder";
import { getInitialBotResponse } from "../../api/api-methods";
import {
  useGenerateAudit,
  useGetInitialBotResponse,
  useSendQuestionResponse,
} from "../../custom-hooks/queries";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { VendorProfileCard } from "../../components/ChatDQP/ChatResponseActions";
import { availableChatResponseTypes } from "../../utils/chat";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import MarkdownText from "../../components/MarkdownText";

// Typing indicator component for bot messages
const TypingIndicator = () => (
  <div className="flex space-x-2 p-3 bg-white border border-gray-200 rounded-lg">
    <div
      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
      style={{ animationDelay: "0ms" }}
    ></div>
    <div
      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
      style={{ animationDelay: "150ms" }}
    ></div>
    <div
      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
      style={{ animationDelay: "300ms" }}
    ></div>
  </div>
);

// Section progress indicator rendered outside of the chat area
const SectionProgressIndicator = ({ sections, currentSectionIndex }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 mb-4">
    <div className="flex items-center justify-center gap-2">
      {sections.map((section, index) => (
        <React.Fragment key={index}>
          <div className="flex items-center">
            <div
              className={`
              h-7 w-7 rounded-full flex items-center justify-center text-sm
              ${
                index < currentSectionIndex
                  ? "bg-green-600 text-white"
                  : index === currentSectionIndex
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-400"
              }
            `}
            >
              {index + 1}
            </div>
            {index === currentSectionIndex && (
              <span className="ml-2 text-sm font-medium text-indigo-600">
                {section.sectionName}
              </span>
            )}
          </div>
          {index < sections.length - 1 && (
            <div className="h-[2px] w-4 bg-gray-200" />
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const PocChatBot = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const questionnaireId = searchParams.get("id");
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const startTimeRef = useRef(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);

  // New state for current section index and completion flag
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const {
    data: questionnaireData,
    isLoading,
    refetch: refetchQuestionnaire,
  } = useGetInitialBotResponse(questionnaireId);

  const sendResponseMutation = useSendQuestionResponse({
    onSuccess: async () => {
      await refetchQuestionnaire();
    },
  });

  // Our generateVqa query uses the custom hook to perform the API call.
  const generateVqa = useGenerateAudit({
    onSuccess: async () => {
      await refetchQuestionnaire();
    },
  });

  // Helper: Find the next unanswered question and update the current section index if needed.
  const findNextQuestion = (sections) => {
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      for (const question of section.questions) {
        if (question.answer === null) {
          if (currentSectionIndex !== i) {
            setCurrentSectionIndex(i);
          }
          return question;
        }
      }
    }
    return null;
  };

  // Helper function to check if answer is a card object
  const isCardAnswer = (answer) => {
    return (
      typeof answer === "object" &&
      answer !== null &&
      answer.type &&
      answer.type === availableChatResponseTypes.vendor_profile_discovery_card
    );
  };

  // Helper function to download Excel from base64
  const handleDownloadExcel = (base64String, fileName) => {
    try {
      // Decode base64 to binary
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Create blob and download
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "vendor-results.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    }
  };

  // Update UI when questionnaire data changes.
  useEffect(() => {
    if (questionnaireData) {
      const initialMessages = [];
      // Iterate over each section and question to build the chat history
      questionnaireData.progressiveSections.forEach((section) => {
        section.questions.forEach((question) => {
          if (question.answer !== null) {
            // Add bot message with the question text
            initialMessages.push({
              id: `${question.question_id}-bot`,
              type: "text",
              content: question.question,
              sender: "bot",
              timestamp: new Date(), // Replace with actual timestamp if available
            });
            // Add user message with the answer
            // Check if answer is a card object or plain text
            if (isCardAnswer(question.answer)) {
              initialMessages.push({
                id: `${question.question_id}-user`,
                type: "card",
                cardType: question.answer.type,
                cardData: question.answer,
                sender: "user",
                timestamp: new Date(),
              });
            } else {
              initialMessages.push({
                id: `${question.question_id}-user`,
                type: "text",
                content: typeof question.answer === "string" ? question.answer : JSON.stringify(question.answer),
                sender: "user",
                timestamp: new Date(), // Replace with actual timestamp if available
              });
            }
          }
        });
      });

      // Determine the next unanswered question for the current bot prompt.
      const nextQuestion = findNextQuestion(
        questionnaireData.progressiveSections
      );
      if (nextQuestion) {
        initialMessages.push({
          id: Date.now(),
          type: "text",
          content: nextQuestion.question,
          sender: "bot",
          timestamp: new Date(),
        });
        setCurrentQuestionId(nextQuestion.question_id);
      } else {
        initialMessages.push({
          id: Date.now(),
          type: "text",
          content: "Thank you! You've completed all the questions.",
          sender: "bot",
          timestamp: new Date(),
        });
        setCurrentQuestionId(null);
        setIsComplete(true);
      }
      // Set the chat history in state
      setMessages(initialMessages);
    }
  }, [questionnaireData]);

  // Send user message and handle bot response.
  const handleSendMessage = async () => {
    if (!inputText.trim() || !currentQuestionId) return;

    const userMessage = {
      id: Date.now(),
      type: "text",
      content: inputText.trim(),
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const userAnswer = inputText.trim();
    setInputText("");

    try {
      const loadingMessage = {
        id: Date.now() + 1,
        type: "typing",
        sender: "bot",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, loadingMessage]);

      // Wait for the mutation to complete before proceeding
      await sendResponseMutation.mutateAsync({
        questionId: currentQuestionId,
        answer: userAnswer,
      });

      // Remove the loading message after the mutation completes
      setMessages((prev) => prev.filter((msg) => msg.type !== "typing"));

    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: "text",
        content: "Sorry, there was an error processing your answer. Please try again.",
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => {
        const filteredMessages = prev.filter((msg) => msg.type !== "typing");
        return [...filteredMessages, errorMessage];
      });
    }
  };

  const [vqaResponse, setVqaResponse] = useState(null);

  const [isGeneratingVqa, setIsGeneratingVqa] = useState(false);

  const handleGenerateVQA = async () => {
    setIsGeneratingVqa(true); // Start the spinner
    try {
      const response = await generateVqa.mutateAsync({ id: questionnaireId });
      setVqaResponse(response); // Save the response
    } catch (error) {
      console.error("Error generating VQA:", error);
    } finally {
      setIsGeneratingVqa(false); // Stop the spinner
    }
  };

  const handleDownloadPDF = () => {
    if (!vqaResponse) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "letter",
    });

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("VQA Response Report", 40, 40);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(
      "Below is the detailed VQA response formatted as a table.",
      40,
      60
    );

    // Prepare table data
    const tableColumns = ["Parameter", "Value"];
    const tableRows = Object.entries(vqaResponse).map(([key, value]) => {
      const formattedValue =
        typeof value === "object"
          ? JSON.stringify(value, null, 2)
          : String(value);
      return [key, formattedValue];
    });

    // Directly call autoTable and pass the doc
    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 80,
      theme: "grid",
      headStyles: { fillColor: [41, 128, 185] },
      styles: { font: "helvetica", fontSize: 10, cellPadding: 5 },
    });

    // Footer
    const pageHeight =
      doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
    doc.setFontSize(10);
    doc.text(
      `Generated on: ${new Date().toLocaleString()}`,
      40,
      pageHeight - 30
    );

    doc.save("vqa-response.pdf");
  };

  // Auto-scroll to bottom on message update.
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (isLoading) {
    return (
      <div className="container">
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container relative">
      {/* Header Section */}
      <div className="p-7 border-y border-gray-300 bg-slate-50 shadow-sm mb-10">
        <div className="flex items-center text-sm mb-4">
          <h1 className="text-gray-500 cursor-pointer font-semibold">POC</h1>
          <ChevronRightIcon className="w-5 h-5 text-gray-400 mx-2" />
          <span className="text-gray-500">
            {questionnaireData?.questionnaireName || "Questionnaire"}
          </span>
        </div>
        <div className="flex items-center">
          <span className="bg-white border border-gray-300 rounded-md p-5 mr-5">
            <SparklesIcon className="h-9 w-9 text-blue-500" />
          </span>
          <div>
            <p className="font-semibold text-xl text-gray-700">
              {
                questionnaireData?.progressiveSections[currentSectionIndex]
                  ?.sectionName
              }
            </p>
            <p className="text-sm text-gray-500">
              Please answer the questions in this section as accurately as
              possible.
            </p>
          </div>
        </div>
      </div>

      {/* Section Progress Indicator (outside of chat area) */}
      {questionnaireData?.progressiveSections && (
        <SectionProgressIndicator
          sections={questionnaireData.progressiveSections}
          currentSectionIndex={currentSectionIndex}
        />
      )}

      {/* Chat History Area */}
      <div className="mb-20 px-5">
        <div className="bg-slate-50 h-[350px] overflow-y-auto p-4 rounded-lg">
          {/* Chat Messages */}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex mb-4 ${
                msg.sender === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {msg.type === "typing" ? (
                <TypingIndicator />
              ) : msg.type === "card" &&
                msg.cardType ===
                  availableChatResponseTypes.vendor_profile_discovery_card ? (
                <div className="max-w-[85%] w-full">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    {/* Card Header */}
                    {msg.cardData.header?.title && (
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {msg.cardData.header.title}
                      </h3>
                    )}
                    
                    {/* Answer text */}
                    {msg.cardData.answer && (
                      <p className="text-sm text-gray-700 mb-4">
                        {msg.cardData.answer}
                      </p>
                    )}

                    {/* Total Results */}
                    {msg.cardData.total_results && (
                      <p className="text-sm font-medium text-gray-600 mb-4">
                        {msg.cardData.total_results}
                      </p>
                    )}

                    {/* Vendor Cards */}
                    {msg.cardData.vendors && msg.cardData.vendors.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {msg.cardData.vendors.map((vendor, idx) => {
                          const vendorData = {
                            vendorType: vendor.vendor_type,
                            id: vendor.provider_id,
                            name: vendor.provider_name,
                            logo: vendor.logo_url,
                            location: vendor.location,
                            serviceCategories:
                              vendor.badges?.service_categories || [],
                            therapeuticAreas:
                              vendor.badges?.therapeutic_areas || [],
                            bio: vendor.bio_quote,
                          };
                          const badgeType =
                            vendor.badges?.service_categories?.length > 0
                              ? "serviceCategories"
                              : "therapeuticAreas";
                          return (
                            <VendorProfileCard
                              key={idx}
                              vendor={vendorData}
                              highlightTerms={[]}
                              badgeType={badgeType}
                            />
                          );
                        })}
                      </div>
                    )}

                    {/* Other Vendors */}
                    {msg.cardData.other_vendors &&
                      msg.cardData.other_vendors.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Other vendors:
                          </p>
                          <ul className="space-y-1.5">
                            {msg.cardData.other_vendors.map((vendor, idx) => (
                              <li
                                key={idx}
                                className="text-sm text-gray-600"
                              >
                                <span className="font-medium text-[#6036C4]">
                                  {vendor.provider_name}
                                </span>
                                {" — "}
                                {vendor.summary}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                    {/* Download Excel Button */}
                    {msg.cardData.excel_file_base64 &&
                      msg.cardData.excel_file_name && (
                        <button
                          onClick={() =>
                            handleDownloadExcel(
                              msg.cardData.excel_file_base64,
                              msg.cardData.excel_file_name
                            )
                          }
                          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors shadow-sm"
                          style={{ backgroundColor: 'rgb(96, 54, 196)' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgb(80, 40, 176)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgb(96, 54, 196)'}
                        >
                          <DocumentArrowDownIcon className="h-5 w-5" />
                          Download Excel
                        </button>
                      )}
                  </div>
                  <p className="mt-1 text-xs text-gray-400 text-right">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              ) : (
                <div
                  className={`max-w-[70%] p-3 rounded-lg ${
                    msg.sender === "user"
                      ? "bg-[rgb(250,243,254)] border border-indigo-200 text-indigo-700"
                      : "bg-white border border-gray-200 text-gray-800"
                  }`}
                >
                  {msg.sender === "bot" ? (
                    <MarkdownText text={msg.content} />
                  ) : (
                    <p>{msg.content}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <div className="max-w-3xl mx-auto flex items-center space-x-4">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
            className="flex-1 p-3 border border-gray-300 rounded-full focus:outline-none focus:border-indigo-500"
            placeholder="Type your answer..."
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim()}
            className="p-3 rounded-full bg-indigo-600 hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
          >
            <PaperAirplaneIcon className="h-6 w-6 text-white" />
          </button>
        </div>
      </div>

      {/* Popup Modal for Generate VQA */}
      {isComplete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full text-center relative">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-indigo-100 rounded-full shadow-lg">
                <svg
                  className="w-8 h-8 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"
                  ></path>
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-3">
              All Questions Answered!
            </h2>
            <p className="text-gray-600 mb-4">
              Click below to generate your VQA report.
            </p>
            <button
              onClick={handleGenerateVQA}
              className="px-5 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-transform transform hover:scale-105 shadow-md"
            >
              Generate VQA
            </button>

            {/* Loader animation while generating VQA */}
            {isGeneratingVqa && (
              <div className="mt-4 flex justify-center">
                <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* Show Download PDF button only when the response is available */}
            {vqaResponse && (
              <button
                onClick={handleDownloadPDF}
                className="bg-blue-500 text-white px-5 py-2 rounded-lg mt-4 font-medium flex items-center justify-center gap-2 hover:bg-blue-600 transition-transform transform hover:scale-105 shadow-md w-full"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v12m0 0l-3-3m3 3l3-3m-6 6h6"
                  ></path>
                </svg>
                Download PDF
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PocChatBot;
